'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { Icon, type IconSvgElement } from '@/components/ui/icon';
import { AngryIcon, NeutralIcon, Relieved01Icon } from '@/components/ui/icon/glyphs';
import { cn } from '@/lib/ui/utils';

/** Quality-of-creation bucket surfaced by the backend's `proposalScore`
 *  field. Wire-typed in `@morfi/backend-sdk-v2`; mirrored here so the UI
 *  layer can type its own props without round-tripping the SDK row type. */
export type ProposalScore = 'good' | 'medium' | 'bad';
interface ScoreDescriptor {
  labelKey: 'good' | 'medium' | 'bad';
  icon: IconSvgElement;
  toneClass: string;
}
const SCORE_DESCRIPTORS: Record<ProposalScore, ScoreDescriptor> = {
  good: {
    labelKey: 'good',
    icon: Relieved01Icon,
    toneClass: 'text-rating-good'
  },
  medium: {
    labelKey: 'medium',
    icon: NeutralIcon,
    toneClass: 'text-rating-medium'
  },
  bad: {
    labelKey: 'bad',
    icon: AngryIcon,
    toneClass: 'text-rating-bad'
  }
};
export interface ProposalScoreBadgeProps {
  score: ProposalScore;
}
export function ProposalScoreBadge(t0) {
  const $ = _c(14);
  const {
    score
  } = t0;
  const t = useTranslations("launchpad.feed.score");
  const descriptor = SCORE_DESCRIPTORS[score];
  const t1 = descriptor.icon;
  let t2;
  if ($[0] !== descriptor.toneClass) {
    t2 = cn("size-4", descriptor.toneClass);
    $[0] = descriptor.toneClass;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== descriptor.icon || $[3] !== t2) {
    t3 = <Icon icon={t1} className={t2} />;
    $[2] = descriptor.icon;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== descriptor.labelKey || $[6] !== t) {
    t4 = t(descriptor.labelKey);
    $[5] = descriptor.labelKey;
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <span className="text-body font-medium text-foreground-primary">{t4}</span>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== score || $[11] !== t3 || $[12] !== t5) {
    t6 = <span className="inline-flex items-center gap-2" data-testid="proposal-score-badge" data-score={score}>{t3}{t5}</span>;
    $[10] = score;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}